import type {
  AiChatResult,
  AiContext,
  AiConversation,
  AiConversationId,
  AiRequest,
  AiResponse,
  AiTask,
} from "./protocol"

type Pending<T> = {
  resolve: (value: T) => void
  reject: (reason?: unknown) => void
  timeout: ReturnType<typeof setTimeout>
  action: AiRequest["action"]
}

export class AiBridge {
  private pending = new Map<string, Pending<AiConversation | AiChatResult>>()
  private destroyed = false

  private static generateId(): string {
    // `crypto.randomUUID()` is not supported in some environments.
    // Fallback: generate a sufficiently unique short id.
    const maybeCrypto = typeof crypto !== "undefined" ? crypto : undefined
    if (maybeCrypto && typeof (maybeCrypto as { randomUUID?: () => string }).randomUUID === "function") {
      return (maybeCrypto as { randomUUID: () => string }).randomUUID()
    }

    const base = Math.random().toString(36).slice(2) + Date.now().toString(36)
    return base.slice(0, 20)
  }

  constructor() {
    window.addEventListener("message", this.handleMessage)
  }

  destroy() {
    this.destroyed = true
    window.removeEventListener("message", this.handleMessage)

    // Reject all in-flight requests to avoid hanging promises and leaked timeouts.
    for (const [requestId, pending] of this.pending.entries()) {
      clearTimeout(pending.timeout)
      try {
        pending.reject("AI bridge destroyed")
      } catch {
        // ignore
      }
      this.pending.delete(requestId)
    }
  }

  history(conversationId: AiConversationId, timeoutMs: number = 3 * 60 * 1000): Promise<AiConversation> {
    const message: AiRequest = {
      type: "AI_REQUEST",
      action: "history",
      requestId: AiBridge.generateId(),
      conversationId,
    }
    return this.post(message, timeoutMs) as Promise<AiConversation>
  }

  chat(
    conversationId: AiConversationId,
    task: AiTask,
    userMessage: string,
    context: AiContext,
    utility: string,
    timeoutMs: number = 3 * 60 * 1000,
  ): Promise<AiChatResult> {
    const message: AiRequest = {
      type: "AI_REQUEST",
      action: "chat",
      requestId: AiBridge.generateId(),
      conversationId,
      task,
      userMessage,
      utility,
      context,
    }
    return this.post(message, timeoutMs) as Promise<AiChatResult>
  }

  /**
   * @deprecated Prefer `chat`. Kept as a thin alias while callers migrate.
   */
  send(
    conversationId: AiConversationId,
    task: AiTask,
    userMessage: string,
    context: AiContext,
    utility: string,
    timeoutMs: number = 3 * 60 * 1000,
  ): Promise<AiChatResult> {
    return this.chat(conversationId, task, userMessage, context, utility, timeoutMs)
  }

  private post(message: AiRequest, timeoutMs: number): Promise<AiConversation | AiChatResult> {
    if (this.destroyed) {
      return Promise.reject("AI bridge destroyed")
    }

    const { requestId, action } = message

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        this.pending.delete(requestId)
        reject("AI timeout")
      }, timeoutMs)

      this.pending.set(requestId, { resolve, reject, timeout, action })
      window.postMessage(message, window.location.origin)
    })
  }

  private handleMessage = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) return

    const message = event.data
    if (!message || typeof message !== "object" || message.type !== "AI_RESPONSE") return
    const response = message as AiResponse

    const pending = this.pending.get(response.requestId)
    if (!pending) return
    if (response.action !== pending.action) return

    clearTimeout(pending.timeout)
    this.pending.delete(response.requestId)

    if (response.success && response.data !== undefined) {
      pending.resolve(response.data)
    } else {
      pending.reject(response.error ?? "AI error")
    }
  }
}

export const aiBridge = new AiBridge()
